/**
 * Responsive breakpoint values (px) for the admin portal.
 *
 * Admin-local: the Eclipse theme ships no breakpoint scale that matches these
 * (its eclipseTier1.breakpoints are 576/768/1024/1440), so the values the portal
 * was laid out against are kept here.
 */
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const

/**
 * Media query helpers for responsive design
 * Usage: ${mediaQueries.up('md')} { ... }
 */
export const mediaQueries = {
  /**
   * Applies styles for screens at or above the specified breakpoint
   */
  up: (breakpoint: keyof typeof breakpoints) =>
    `@media (min-width: ${breakpoints[breakpoint]}px)`,

  /**
   * Applies styles for screens below the specified breakpoint
   */
  down: (breakpoint: keyof typeof breakpoints) =>
    `@media (max-width: ${breakpoints[breakpoint] - 1}px)`,

  /**
   * Applies styles for screens between two breakpoints
   */
  between: (min: keyof typeof breakpoints, max: keyof typeof breakpoints) =>
    `@media (min-width: ${breakpoints[min]}px) and (max-width: ${breakpoints[max] - 1}px)`,

  /**
   * Applies styles for screens at exactly the specified breakpoint range
   */
  only: (breakpoint: keyof typeof breakpoints) => {
    const keys = Object.keys(breakpoints) as Array<keyof typeof breakpoints>
    const index = keys.indexOf(breakpoint)
    const nextBreakpoint = keys[index + 1]

    if (!nextBreakpoint) {
      return mediaQueries.up(breakpoint)
    }

    return mediaQueries.between(breakpoint, nextBreakpoint)
  },
} as const

/**
 * Predefined responsive breakpoint ranges
 */
export const responsiveRanges = {
  mobile: mediaQueries.down('md'), // 0 - 767px
  tablet: mediaQueries.between('md', 'lg'), // 768 - 1023px
  desktop: mediaQueries.up('lg'), // 1024px+
  mobileAndTablet: mediaQueries.down('lg'), // 0 - 1023px
  tabletAndDesktop: mediaQueries.up('md'), // 768px+
} as const

/**
 * Helper to check if a screen size matches a breakpoint
 * Usage in components: const isMobile = useMediaQuery(responsiveRanges.mobile)
 */
export const createMediaQueryMatcher = (query: string): boolean => {
  if (typeof window === 'undefined') {return false}
  return window.matchMedia(query.replace('@media ', '')).matches
}
